import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface Props {
  succeededTargets: string[];
  failedTarget: string;
  errorMessage: string;
  backupTaken: boolean;
  onClose: () => void;
  onRollback: (targets: string[]) => Promise<void>;
}

const RollbackPromptModal: React.FC<Props> = ({ succeededTargets, failedTarget, errorMessage, backupTaken, onClose, onRollback }) => {
  const [busy, setBusy] = useState(false);
  const handleRollback = async () => {
    setBusy(true);
    try {
      await onRollback(succeededTargets);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Run failed</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 text-sm text-slate-700">
          <p>
            Tenant <code className="font-mono">{failedTarget}</code> failed:
          </p>
          <pre className="text-xs font-mono bg-red-50 text-red-700 border border-red-200 rounded p-2 whitespace-pre-wrap">{errorMessage}</pre>
          <p>
            {succeededTargets.length} tenant(s) completed successfully before this. Roll them back?
          </p>
          {!backupTaken && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Backups were not taken for this run, so rollback is not available. Re-run with the backup checkbox enabled to make rollback possible.
            </p>
          )}
        </div>
        <div className="flex gap-2 justify-end px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50">
            Leave as-is
          </button>
          <button
            onClick={handleRollback}
            disabled={!backupTaken || succeededTargets.length === 0 || busy}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? 'Rolling back…' : `Roll back ${succeededTargets.length} tenants`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RollbackPromptModal;
